import { useEffect, useState } from "react";
import { CircleAlert } from "lucide-react";

import { Alert, AlertDescription } from "~/components/ui/alert";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Textarea } from "~/components/ui/textarea";

export interface DeletePesTestDialogProps {
  isOpen: boolean;
  isFailure: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => Promise<void> | void;
  error?: string | null;
  loading?: boolean;
}

export const DeletePesTestDialog = ({
  isOpen,
  isFailure,
  onCancel,
  onConfirm,
  error,
  loading = false,
}: DeletePesTestDialogProps) => {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (isOpen) {
      setReason("");
    }
  }, [isOpen]);

  const trimmedReason = reason.trim();
  const canConfirm = trimmedReason.length > 0 && !loading;

  const handleOpenChange = (open: boolean) => {
    if (!open && !loading) {
      onCancel();
    }
  };

  const handleConfirm = async () => {
    if (!canConfirm) {
      return;
    }

    await onConfirm(trimmedReason);
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete test</AlertDialogTitle>
          <AlertDialogDescription>
            Deleting a test is permanent and irreversible. Are you sure you
            would like to continue?
          </AlertDialogDescription>
        </AlertDialogHeader>

        {!isFailure && (
          <Alert variant="destructive">
            <CircleAlert aria-hidden="true" />
            <AlertDescription>
              Deleting a successful test will refund this test to the user&apos;s
              cassette
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <label
            htmlFor="delete-pes-test-reason"
            className="text-sm font-medium text-foreground"
          >
            Reason for deletion
          </label>
          <Textarea
            id="delete-pes-test-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Explain why this test is being deleted"
            disabled={loading}
            rows={4}
          />
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}

        <AlertDialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {loading ? "Deleting..." : "Delete test"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
